import React, { useEffect, useRef, useState } from "react";

/**
 * ProtectedVideo
 * - Disables right-click / drag / picture-in-picture / native download button
 * - Supports YouTube (via IFrame API) and direct video URLs
 * - Optional onProgress callback with % watched (0-100)
 * - Adaptive aspect ratio:
 *    - YouTube: 16:9 default (or 9:16 auto for /shorts/)
 *    - MP4/direct: detects videoWidth/videoHeight dynamically
 *    - override with prop `aspectRatio` (ex: "9/16", "4/5")
 */
function parseYT(url) {
  const m = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
}
function parseVimeo(url) {
  const m = url?.match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
}

// Load YT iframe API once
let ytApiPromise = null;
function loadYTApi() {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev && prev(); resolve(window.YT); };
    const s = document.createElement("script");
    s.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(s);
  });
  return ytApiPromise;
}

export default function ProtectedVideo({ url, onProgress, className = "", aspectRatio }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const [maxProgress, setMaxProgress] = useState(0);
  const [videoRatio, setVideoRatio] = useState(null); // ex: "1920/1080"

  const ytId = parseYT(url);
  const vmId = parseVimeo(url);
  const isYtShorts = /\/shorts\//.test(url || "");

  // Aspect ratio final (prop > detectado > default 16/9 ou 9/16 shorts)
  const finalRatio =
    aspectRatio ||
    videoRatio ||
    (isYtShorts ? "9/16" : "16/9");

  useEffect(() => {
    let interval;
    if (ytId) {
      loadYTApi().then((YT) => {
        if (!containerRef.current) return;
        playerRef.current = new YT.Player(containerRef.current, {
          videoId: ytId,
          width: "100%",
          height: "100%",
          host: "https://www.youtube-nocookie.com",
          playerVars: {
            modestbranding: 1, rel: 0, controls: 1, disablekb: 1,
            fs: 1, iv_load_policy: 3, playsinline: 1, showinfo: 0,
            origin: window.location.origin,
          },
          events: {
            onReady: () => {
              interval = setInterval(() => {
                try {
                  const p = playerRef.current;
                  if (!p || !p.getDuration) return;
                  const dur = p.getDuration();
                  const t = p.getCurrentTime();
                  if (dur > 0) {
                    const pct = Math.min(100, Math.round((t / dur) * 100));
                    setMaxProgress((m) => {
                      const nm = Math.max(m, pct);
                      onProgress && onProgress(nm);
                      return nm;
                    });
                  }
                } catch (_) {}
              }, 700);
            },
          },
        });
      });
    }
    return () => { if (interval) clearInterval(interval); };
    // eslint-disable-next-line
  }, [ytId]);

  const handleTime = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = Math.min(100, Math.round((v.currentTime / v.duration) * 100));
    setMaxProgress((m) => {
      const nm = Math.max(m, pct);
      onProgress && onProgress(nm);
      return nm;
    });
  };

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (v && v.videoWidth && v.videoHeight) {
      setVideoRatio(`${v.videoWidth}/${v.videoHeight}`);
    }
  };

  const stopEvt = (e) => { e.preventDefault(); return false; };

  // Estilo do container — respeita aspect ratio dinamicamente
  const containerStyle = { aspectRatio: finalRatio };
  // Para vídeos verticais (mais altos que largos), limita largura
  const isVertical = (() => {
    try {
      const [w, h] = finalRatio.split("/").map(Number);
      return h > w;
    } catch { return false; }
  })();
  const wrapperClass = isVertical
    ? "max-w-[420px] mx-auto"
    : "w-full";

  return (
    <div
      className={`relative select-none ${wrapperClass} ${className}`}
      onContextMenu={stopEvt}
      onDragStart={stopEvt}
      style={{ WebkitUserSelect: "none", userSelect: "none" }}
    >
      {ytId ? (
        <div
          className="relative w-full rounded-sm overflow-hidden bg-black"
          style={containerStyle}
        >
          <div ref={containerRef} className="absolute inset-0" />
          {/* Block clicks on the YouTube title bar (top) */}
          <div
            className="absolute top-0 left-0 right-0 h-14 cursor-not-allowed"
            style={{ zIndex: 10, background: "transparent" }}
            onClick={stopEvt}
            onContextMenu={stopEvt}
            data-testid="yt-top-block"
          />
          {/* Block clicks on the "Watch on YouTube" button (bottom-right) */}
          <div
            className="absolute bottom-2 right-2 w-44 h-10 cursor-not-allowed"
            style={{ zIndex: 10, background: "transparent" }}
            onClick={stopEvt}
            onContextMenu={stopEvt}
            data-testid="yt-bottom-block"
          />
        </div>
      ) : vmId ? (
        <iframe
          title="video"
          className="w-full rounded-sm"
          style={containerStyle}
          src={`https://player.vimeo.com/video/${vmId}?dnt=1`}
          allow="autoplay; encrypted-media; fullscreen"
          allowFullScreen
        />
      ) : (
        <video
          ref={videoRef}
          className="w-full rounded-sm bg-black"
          style={containerStyle}
          src={url}
          controls
          controlsList="nodownload noplaybackrate noremoteplayback"
          disablePictureInPicture
          disableRemotePlayback
          onTimeUpdate={handleTime}
          onLoadedMetadata={handleLoadedMetadata}
          onContextMenu={stopEvt}
        />
      )}
    </div>
  );
}
